/**
 * Dialog B — Choose the bank account.
 *
 * Lists the connection's accounts and links ONE of them to the ledger account
 * whose feed tab opened this dialog. Also the place the link is undone.
 *
 * ── TWO DOORS, TWO SHELLS — THE SUBTLEST THING IN THIS FILE ─────────────────
 * `journey` is what tells them apart, and the shell follows it:
 *
 * 1. **In the flow** (`journey` set, from `BankFeedConnectionDialogs`) this is
 *    the LAST step of the connect journey, resumed after the redirect to the
 *    bank. It wears the journey's FULLSCREEN band — same logo, same step strip
 *    the user left on Dialog A and saw again on `/bank-callback`. The wizard's
 *    STATE could never survive that redirect; its CHROME is how the trip still
 *    reads as one journey.
 * 2. **The direct door** (`journey` absent) is Edit Account's Bank feed
 *    section, which is ITSELF a `FullscreenFormDialog`. Going fullscreen there
 *    would stack fullscreen on fullscreen, covering a modal holding unsaved
 *    form state — so that door stays a `StandardDialog`, and with no trip to
 *    the bank behind it there are no steps to count either.
 *
 * `openChooseAccount` has no caller outside `useBankFeedConnectionFlow`, so
 * these two really are the only doors: in flow it is always the last step, at
 * the direct door always no journey at all.
 *
 * WHY THE PICK IS MANUAL. `chart_of_accounts` holds no IBAN, BIC or external
 * account id, so there is nothing to auto-match on: the best guess below only
 * pre-selects, it never links silently. After the first link it is permanent —
 * the account is re-found across reconnects by its identification hash.
 *
 * Mount conditionally (`{isOpen && <ChooseAccountDialog … />}`): the body calls
 * data hooks.
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, CheckCircle, Landmark, Link2Off, Loader2, RefreshCw } from 'lucide-react';

import {
  FullscreenDialog,
  FullscreenDialogContent,
  FullscreenDialogTitle,
  FullscreenDialogDescription,
  FullscreenDialogBody,
  FullscreenDialogFooter,
} from '@/components/ui/fullscreen-dialog';
import {
  StandardDialog,
  StandardDialogContent,
  StandardDialogHeader,
  StandardDialogTitle,
  StandardDialogDescription,
  StandardDialogBody,
  StandardDialogFooter,
} from '@/components/ui/standard-dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useShowAccountNumbers } from '@/hooks/useShowAccountNumbers';
import { useChartOfAccounts } from '@/hooks/useChartOfAccounts';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';
import { useLinkBankConnectionAccount } from '@/hooks/useBankConnections';
import {
  isCreditCardType,
  isFeedAccountType,
  type BankConnectionAccountWire,
  type BankConnectionWire,
} from '@releve/shared';
import { ConnectFlowHeader } from './ConnectFlowHeader';
import {
  describeAccount,
  describeLedgerAccount,
  linkBlockReason,
  pickSuggestedAccountId,
  type ConnectFlowJourney,
  type LinkBlockReason,
  type Translate,
} from './connectHelpers';

export interface ChooseAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The ledger (feed) account this dialog links a bank account TO. */
  bankAccountId: string;
  /**
   * EVERY connection whose accounts are on offer — an org can hold consent at
   * several banks at once, and listing only the most recent one made the others'
   * accounts unreachable (they looked as though their consent had been revoked).
   * Rows are grouped by bank; the caller passes the org's live connections plus,
   * when this ledger account is already linked, its own connection so Unlink
   * stays reachable even if that consent has since lapsed.
   */
  connections: BankConnectionWire[];
  /** Fired after a successful link, with the updated row. */
  onLinked?: (account: BankConnectionAccountWire) => void;
  /** Fired after a successful unlink. */
  onUnlinked?: (account: BankConnectionAccountWire) => void;
  /**
   * Opens the reconnect flow for this connection. The list on offer is what the
   * bank returned at the LAST authorisation — nothing here talks to Enable
   * Banking — so an account opened since, or one left out of the consent, only
   * appears after a fresh trip to the bank. When absent the hint is not shown.
   */
  onReconnect?: () => void;
  /**
   * The journey this dialog is the LAST step of — which is also what picks the
   * shell (see the header comment). ABSENT ⇒ the direct door from
   * `AccountBankFeedSection`: a centred `StandardDialog` with no step strip,
   * because there is no journey to be a step of.
   */
  journey?: ConnectFlowJourney | null;
}

export function ChooseAccountDialog({
  open,
  onOpenChange,
  organizationId,
  bankAccountId,
  connections,
  onLinked,
  onUnlinked,
  onReconnect,
  journey,
}: ChooseAccountDialogProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const showAccountNumbers = useShowAccountNumbers();
  const { enabled: featureEnabled } = useFeatureEnabled('bank_feed_sync');
  const { data: accounts, isLoading: accountsLoading } = useChartOfAccounts(organizationId);
  const linkAccount = useLinkBankConnectionAccount(organizationId);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);

  const ledgerAccount = useMemo(
    () => (accounts ?? []).find((account) => account.id === bankAccountId) ?? null,
    [accounts, bankAccountId],
  );
  // The CoA column is nullable — an account that states no currency uses the
  // organization's.
  const ledgerCurrency =
    ledgerAccount?.currency ?? selectedOrganization?.currency ?? null;
  const ledgerTypeName = ledgerAccount?.type?.type_name ?? null;
  const isFeedAccount = isFeedAccountType(ledgerTypeName);
  const isCard = isCreditCardType(ledgerTypeName);

  // Flattened once, each row remembering which bank it came from: the link
  // mutation keys on `bank_connection_accounts.id`, which is unique across
  // connections, so nothing downstream needs the grouping.
  const groups = useMemo(
    () =>
      connections
        .map((c) => ({ connection: c, accounts: c.accounts ?? [] }))
        .filter((g) => g.accounts.length > 0),
    [connections],
  );
  const connectionAccounts = useMemo(() => groups.flatMap((g) => g.accounts), [groups]);

  const linkedAccount = useMemo(
    () =>
      connectionAccounts.find((account) => account.chart_of_accounts_id === bankAccountId) ??
      null,
    [connectionAccounts, bankAccountId],
  );

  const suggestedId = useMemo(
    () =>
      ledgerAccount
        ? pickSuggestedAccountId(connectionAccounts, {
            id: ledgerAccount.id,
            name: ledgerAccount.account_name,
            code: ledgerAccount.account_number,
            currency: ledgerCurrency,
          })
        : null,
    [connectionAccounts, ledgerAccount, ledgerCurrency],
  );

  const schema = useMemo(
    () =>
      z.object({
        connection_account_id: z
          .string()
          .min(1, { message: t('bankAccounts.sync.chooseAccount.selectionRequired') }),
        flip_card_sign: z.boolean(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      connection_account_id: linkedAccount?.id ?? suggestedId ?? '',
      flip_card_sign: linkedAccount?.flip_card_sign ?? false,
    },
  });

  // The chart of accounts is usually still in flight on the first render (the
  // dialog is conditionally mounted, so its queries start when it opens), and
  // `defaultValues` are read once. Without this the pre-selection would be lost
  // exactly when the reference data is cold — the common case.
  useEffect(() => {
    if (!suggestedId) return;
    if (form.getValues('connection_account_id')) return;
    form.setValue('connection_account_id', suggestedId);
  }, [suggestedId, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      const { account } = await linkAccount.mutateAsync({
        accountId: values.connection_account_id,
        chartOfAccountsId: bankAccountId,
        // Only meaningful for a credit-card target; the direction flip is about
        // card statements, never bank ones.
        flipCardSign: isCard ? values.flip_card_sign : false,
      });
      onLinked?.(account);
      onOpenChange(false);
    } catch {
      // `useLinkBankConnectionAccount` owns both toasts (success and failure)
      // and the Sentry capture. Catching here only stops the rejection
      // escaping the submit handler — the dialog stays open for a retry.
    }
  };

  const onUnlink = async () => {
    if (!linkedAccount) return;
    try {
      const { account } = await linkAccount.mutateAsync({
        accountId: linkedAccount.id,
        chartOfAccountsId: null,
      });
      onUnlinked?.(account);
      onOpenChange(false);
    } catch {
      // Reported by the hook; see onSubmit.
    }
  };

  if (!featureEnabled) return null;

  const submitting = linkAccount.isPending;
  const loading = accountsLoading;
  const hasAccounts = connectionAccounts.length > 0;
  const showPicker =
    Boolean(ledgerAccount) && isFeedAccount && !linkedAccount && hasAccounts;

  // Link is only meaningful once a SELECTABLE account is actually chosen.
  // Blocked rows (linked elsewhere, currency mismatch) render as disabled
  // radios, so a consent whose every account is blocked — or one where nothing
  // was pre-selected because no candidate scored — would otherwise present a
  // live "Link account" whose only possible outcome is a zod error under the
  // field. Gate the button on the same predicate the rows are disabled by, so
  // the two can never disagree.
  const selectableAccounts = connectionAccounts.filter(
    (account) => linkBlockReason(account, bankAccountId, ledgerCurrency) === null,
  );
  const selectedAccountId = form.watch('connection_account_id');
  const canLink =
    selectedAccountId !== '' &&
    selectableAccounts.some((account) => account.id === selectedAccountId);

  // ONE body and ONE footer, mounted by whichever of the two shells the journey
  // selects (see the header comment). Building them once is what keeps the two
  // doors from drifting into two different pickers.
  const body = (
    <>
      {ledgerAccount && (
        <div className="rounded-md border bg-muted/40 p-3">
          <p className="text-xs font-normal text-muted-foreground">
            {t('bankAccounts.sync.chooseAccount.ledgerAccountLabel')}
          </p>
          <p className="text-sm font-medium">
            {describeLedgerAccount(ledgerAccount, showAccountNumbers)}
            {ledgerCurrency ? ` · ${ledgerCurrency}` : ''}
          </p>
        </div>
      )}

      {loading && !ledgerAccount && (
        <p className="text-sm text-muted-foreground">
          {t('bankAccounts.sync.chooseAccount.loading')}
        </p>
      )}

      {!loading && !ledgerAccount && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{t('bankAccounts.sync.chooseAccount.ledgerAccountMissing')}</AlertTitle>
        </Alert>
      )}

      {/* Same guard the link route enforces — surfaced before the click
          rather than as a 400. */}
      {ledgerAccount && !isFeedAccount && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{t('bankAccounts.sync.chooseAccount.notFeedAccountTitle')}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t('bankAccounts.sync.chooseAccount.notFeedAccountDescription')}
          </AlertDescription>
        </Alert>
      )}

      {ledgerAccount && isFeedAccount && linkedAccount && (
        <div className="space-y-3">
          <div className="rounded-md border p-3 space-y-1">
            <p className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle className="h-4 w-4 text-success" />
              {t('bankAccounts.sync.chooseAccount.linkedTitle')}
            </p>
            <p className="text-sm">{describeAccount(linkedAccount, t)}</p>
            <p className="text-xs font-normal text-muted-foreground">
              {t('bankAccounts.sync.chooseAccount.linkedDescription')}
            </p>
          </div>

          {confirmingUnlink ? (
            <Alert variant="warning" className="space-y-2">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>
                {t('bankAccounts.sync.chooseAccount.unlinkWarningTitle')}
              </AlertTitle>
              <p className="text-xs font-normal text-muted-foreground">
                {t('bankAccounts.sync.chooseAccount.unlinkWarningDescription')}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmingUnlink(false)}
                  disabled={submitting}
                >
                  {t('bankAccounts.sync.chooseAccount.unlinkCancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onUnlink}
                  disabled={submitting}
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('bankAccounts.sync.chooseAccount.unlinkConfirm')}
                </Button>
              </div>
            </Alert>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmingUnlink(true)}
              disabled={submitting}
            >
              <Link2Off className="h-4 w-4 mr-2" />
              {t('bankAccounts.sync.chooseAccount.unlink')}
            </Button>
          )}
        </div>
      )}

      {/* The centred empty state is reserved for exactly this case
          (docs/STYLES.md "Empty States"): the connection authorized but
          returned nothing linkable. The feed grid owns every other
          zero-row state. */}
      {ledgerAccount && isFeedAccount && !linkedAccount && !hasAccounts && !loading && (
        <div className="text-center py-8">
          <Landmark className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {t('bankAccounts.sync.chooseAccount.emptyTitle')}
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {t('bankAccounts.sync.chooseAccount.emptyDescription')}
          </p>
          {onReconnect && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={onReconnect}
              disabled={submitting}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('bankAccounts.sync.chooseAccount.refreshAction')}
            </Button>
          )}
        </div>
      )}

      {showPicker && (
        <FormField
          control={form.control}
          name="connection_account_id"
          render={({ field, fieldState }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t('bankAccounts.sync.chooseAccount.accountsLabel')}</FormLabel>
                {fieldState.error && (
                  <span className="text-xs text-destructive">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="space-y-2"
                >
                  {/* Grouped by bank. The heading only earns its space
                      once there is more than one — a single-bank org
                      should not grow a redundant label. */}
                  {groups.map((group) => (
                    <div key={group.connection.id} className="space-y-2">
                      {groups.length > 1 && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {`${group.connection.aspsp_name} · ${group.connection.aspsp_country}`}
                        </p>
                      )}
                      {group.accounts.map((account) => {
                        const blocked = linkBlockReason(
                          account,
                          bankAccountId,
                          ledgerCurrency,
                        );
                        return (
                          <div
                            key={account.id}
                            className="flex items-start gap-2 rounded-md border p-3"
                          >
                            <RadioGroupItem
                              value={account.id}
                              id={`eb-account-${account.id}`}
                              className="mt-1"
                              disabled={blocked !== null}
                            />
                            <div className="space-y-0.5">
                              <Label
                                htmlFor={`eb-account-${account.id}`}
                                className="font-normal cursor-pointer"
                              >
                                {describeAccount(account, t)}
                              </Label>
                              {blocked && (
                                <p className="text-xs text-destructive">
                                  {blockedMessage(blocked, account, ledgerCurrency, t)}
                                </p>
                              )}
                              {!blocked && account.currency.toUpperCase() === 'XXX' && (
                                <p className="text-xs font-normal text-muted-foreground">
                                  {t('bankAccounts.sync.chooseAccount.multiCurrency')}
                                </p>
                              )}
                              {!blocked && !account.is_in_current_consent && (
                                <p className="text-xs font-normal text-muted-foreground">
                                  {t('bankAccounts.sync.chooseAccount.notInConsent')}
                                </p>
                              )}
                              {!blocked && account.id === suggestedId && (
                                <p className="text-xs font-normal text-muted-foreground">
                                  {t('bankAccounts.sync.chooseAccount.suggested')}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {/* BELOW the list on purpose: the copy points at the rows
          ("each account above says why"), and a disabled Link with no
          explanation reads as a bug. The bank-returned-nothing case
          keeps its own centred empty state instead. */}
      {showPicker && selectableAccounts.length === 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{t('bankAccounts.sync.chooseAccount.noneSelectableTitle')}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t('bankAccounts.sync.chooseAccount.noneSelectableDescription')}
          </AlertDescription>
        </Alert>
      )}

      {/* The list is a snapshot from the last authorisation (see the
          `onReconnect` prop), so say how to refresh it rather than let a
          missing account read as a bug. Plain text + a link-styled
          action, matching FeedContextBar's fact-row treatment. */}
      {showPicker && onReconnect && (
        <p className="text-xs font-normal text-muted-foreground">
          {t('bankAccounts.sync.chooseAccount.refreshHint')}{' '}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={onReconnect}
            disabled={submitting}
          >
            {t('bankAccounts.sync.chooseAccount.refreshAction')}
          </Button>
        </p>
      )}

      {/* The flip is about the STATEMENT's direction, and only card
          statements need it (packages/shared bankImportSign). */}
      {showPicker && isCard && (
        <FormField
          control={form.control}
          name="flip_card_sign"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-start gap-2">
                <FormControl>
                  <Checkbox
                    id="eb-flip-card-sign"
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    className="mt-0.5"
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <Label
                    htmlFor="eb-flip-card-sign"
                    className="font-normal cursor-pointer"
                  >
                    {t('bankAccounts.sync.chooseAccount.flipCardSignLabel')}
                  </Label>
                  <FormDescription>
                    {t('bankAccounts.sync.chooseAccount.flipCardSignHelp')}
                  </FormDescription>
                </div>
              </div>
            </FormItem>
          )}
        />
      )}
    </>
  );

  const footer = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={submitting}
      >
        {showPicker
          ? t('bankAccounts.sync.chooseAccount.cancel')
          : t('bankAccounts.sync.chooseAccount.close')}
      </Button>
      {showPicker && (
        <Button type="submit" disabled={submitting || !canLink}>
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitting
            ? t('bankAccounts.sync.chooseAccount.linking')
            : t('bankAccounts.sync.chooseAccount.link')}
        </Button>
      )}
    </>
  );

  if (journey) {
    return (
      <FullscreenDialog open={open} onOpenChange={onOpenChange}>
        <FullscreenDialogContent>
          {/* Replaces `FullscreenDialogHeader` — it paints the same band and
              adds the journey's logo, strip and close. */}
          <ConnectFlowHeader
            title={
              <FullscreenDialogTitle>
                {t('bankAccounts.sync.chooseAccount.title')}
              </FullscreenDialogTitle>
            }
            journey={journey}
            // Structural, not counted: B only ever closes a journey.
            currentStep="last"
            onClose={() => onOpenChange(false)}
          />

          <Form {...form}>
            {/* `contents` keeps Body and Footer as direct flex children of
                Content — the fullscreen Content is the same `flex flex-col`,
                so this wiring is unchanged from the standard shell below. */}
            <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
              <FullscreenDialogBody className="p-4 md:p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                  {/* The step names itself in the body, as the other steps of
                      this journey do — but only on THIS door: the standard
                      shell below already puts title and description in its own
                      header, and a second heading there would just repeat it.
                      The description is CONTENT, not a band subtitle (the band
                      has no room and `sr-only` would silently drop it), and
                      stays a Radix `Description` so it names the dialog. */}
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-semibold">
                      {t('bankAccounts.sync.chooseAccount.heading')}
                    </h2>
                    <FullscreenDialogDescription>
                      {t('bankAccounts.sync.chooseAccount.description')}
                    </FullscreenDialogDescription>
                  </div>
                  {/* The picker keeps its own 16px rhythm inside the 24px one
                      the step header sits in — the two-level spacing a CAMT
                      step gets from its Cards. */}
                  <div className="space-y-4">{body}</div>
                </div>
              </FullscreenDialogBody>

              <FullscreenDialogFooter>{footer}</FullscreenDialogFooter>
            </form>
          </Form>
        </FullscreenDialogContent>
      </FullscreenDialog>
    );
  }

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="lg">
        <StandardDialogHeader>
          <StandardDialogTitle>
            {t('bankAccounts.sync.chooseAccount.title')}
          </StandardDialogTitle>
          <StandardDialogDescription>
            {t('bankAccounts.sync.chooseAccount.description')}
          </StandardDialogDescription>
        </StandardDialogHeader>

        <Form {...form}>
          {/* `contents` keeps Body and Footer as direct flex children of
              Content — docs/STANDARD_MODALS.md "Wiring the form". */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
            <StandardDialogBody className="space-y-4">{body}</StandardDialogBody>

            <StandardDialogFooter>{footer}</StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}

function blockedMessage(
  reason: LinkBlockReason,
  account: BankConnectionAccountWire,
  ledgerCurrency: string | null,
  t: Translate,
): string {
  if (reason === 'linkedElsewhere') {
    return t('bankAccounts.sync.chooseAccount.alreadyLinkedElsewhere');
  }
  return t('bankAccounts.sync.chooseAccount.currencyMismatch', {
    bankCurrency: account.currency,
    ledgerCurrency: ledgerCurrency ?? '',
  });
}

/**
 * The tinted-container treatment `FeedContextBar` already uses for a fact that
 * needs escalating — deliberately not a Badge.
 */
